import { forwardRef } from "react";
import type React from "react";
import cn from "mcn";

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps = Omit<React.ComponentProps<"select">, "placeholder"> & {
  options: SelectOption[];
  error?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, error, fullWidth, placeholder, className, ...props },
  ref,
) {
  return (
    <div className={cn("relative", fullWidth && "w-full")}>
      <select
        ref={ref}
        {...props}
        className={cn(
          "appearance-none w-full rounded-lg border px-3 py-1.5 pr-9 text-sm transition-colors cursor-pointer",
          "bg-[var(--color-surface-1)] text-[var(--color-foreground)]",
          "border-[var(--color-outline)]",
          "hover:bg-[var(--color-surface-2)] hover:border-[var(--color-outline-variant)]",
          "focus:outline-none focus:border-[var(--color-outline)] focus:ring-1 focus:ring-[var(--color-outline)]",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[var(--color-neutral-disabled)] disabled:text-[var(--color-neutral-on-disabled)] disabled:border-[var(--color-neutral-on-disabled)]",
          error && "border-[var(--color-danger)]",
          className,
        )}
        style={{
          ...props.style,
        }}
      >
        {!props.value && placeholder && (
          <option value="" disabled style={{ color: "var(--color-foreground-muted)" }}>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <div
        className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center"
        style={{ width: "24px", height: "24px" }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ fill: props.disabled ? "var(--color-neutral-on-disabled)" : "var(--color-neutral)" }}
          aria-hidden="true"
        >
          <title>Dropdown arrow</title>
          <path d="M7 10l5 5 5-5z" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
});
